import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import Select from "react-select";
import {
  CheckPermission,
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { ApiUnit } from "../../unit/ApiUnit";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const CustomInput = ({ autoFocus, label, name, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      autoFocus={autoFocus}
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const OtherIncomeHelpFromMuqamiTanzeem = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [formData, setFormData] = useState({
    selectedFinanceCategory: null,
    selectedAccount: null,
    selectedBankAccount: null,
    selectedUnit: null,
    date: new Date(),
    amount: "",
    memo: "",
    helpFromMuqamiTanzeemId: 0,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const payload = {
      financeAccount: formData.selectedAccount ?? null,
      category: formData.selectedFinanceCategory ?? null,
      bankAccount: formData.selectedBankAccount
        ? {
            value: formData.selectedBankAccount.value,
            label: formData.selectedBankAccount.label,
          }
        : null,
      date: formData.date,
      amount: formData.amount,
      memo: formData.memo,
      unitId: loggedInUnitId,
      helpFromMuqamiTanzeemId: formData.selectedUnit?.value,
    };

    setLoading(true);
    ApiFinanceLocal()
      .AddOtherIncomeHelpFromMuqamiTanzeem(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === "200") {
          swal({
            title: "Success",
            text:
              t(data.message) ||
              t(
                "finance_other_income_assistance_from_muqami_tanzeem_requested",
              ),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Other Income",
          icon: "error",
          buttons: "OK",
        });

        setLoading(false);
      })
      .catch((err) => {
        const errorMsg =
          err?.response?.data?.message ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          "Unable to connect to server. Please try again.";
        swal({
          title: "Error",
          text: errorMsg,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoriesListByType("income")
      .then((res) => {
        const preferred = res.data.find(
          (c) => c.label1 && c.label1.includes("help_from_muqami_tanzeem"),
        );
        setFormData((prev) => ({
          ...prev,
          selectedFinanceCategory: preferred || res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading(false));
  }, [unitTypeName]);

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setAccountsLoading(true);
    setFormData((prev) => ({
      ...prev,
      selectedAccount: null,
    }));

    ApiFinance()
      .GetFinanceSubCategoryAccountsById(categoryId)
      .then((res) => {
        const preferred = res.data.find((c) => c.value === 1096);
        setFormData((prev) => ({
          ...prev,
          selectedAccount: preferred || res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setAccountsLoading(false));
  }, [formData.selectedFinanceCategory]);

  useEffect(() => {
    if (!loggedInUnitId) return;
    const targetUnitType = "Tanzeem";
    ApiUnit()
      .GetChildUnitsByType(loggedInUnitId, targetUnitType, true)
      .then((res) => {
        const units = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.unitList)
            ? res.data.unitList
            : [];

        const options = units.map((unit) => ({
          value: unit.id ?? unit.value,
          label: unit.name ?? unit.label,
        }));

        setUnitOptions(options);
        if (options.length > 0) {
          setFormData((prev) => ({
            ...prev,
            selectedUnit: options[0],
          }));
        }
      })
      .catch((err) => {
        console.error(err);
        setUnitOptions([]);
      });
  }, [loggedInUnitId]);

  useEffect(() => {
    if (!loggedInUnitId) return;
    ApiFinanceLocal()
      .GetFinanceBankAccounts(loggedInUnitId)
      .then((res) => {
        const rawAccounts = Array.isArray(res.data) ? res.data : [];
        const accounts = rawAccounts.filter(
          (a) =>
            a.label1 === "cash_in_hand_account" ||
            a.label1 === "amanat_account" ||
            a.label1 === "cash_in_hand" ||
            a.label1 === "amanat" ||
            (a.label &&
              (a.label.toLowerCase().includes("amanat") ||
                a.label.toLowerCase().includes("cash"))),
        );
        setBankAccounts(accounts);
        if (accounts.length > 0) {
          const defaultAcc =
            accounts.find(
              (a) =>
                a.label1 === "cash_in_hand_account" ||
                a.label1 === "cash_in_hand",
            ) || accounts[0];
          setFormData((prev) => ({
            ...prev,
            selectedBankAccount: prev.selectedBankAccount || defaultAcc,
          }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [loggedInUnitId]);

  const validate = () => {
    const temp = {};
    temp.selectedUnit = formData.selectedUnit ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );
    temp.selectedBankAccount = formData.selectedBankAccount ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );
    temp.amount = formData.amount ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_amount_paid_error" />
    );

    temp.memo =
      formData.memo && formData.memo.trim() !== "" ? (
        ""
      ) : (
        <GetLanguageString props="common_field_required_error" />
      );

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {
              <GetLanguageString props="funds_other_income_assistance_from_muqami_tanzeem" />
            }
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="select_unit_muqami_tanzeem" />}
                    </Label>
                    <Select
                      components={{
                        Option: customOption,
                        SingleValue: customSingleValue,
                      }}
                      options={unitOptions}
                      value={formData.selectedUnit}
                      onChange={(selected) =>
                        handleChange("selectedUnit", selected)
                      }
                      classNamePrefix="select"
                    />
                    {errors.selectedUnit && (
                      <h4 className="text-error no-drag">
                        {errors.selectedUnit}
                      </h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_bank_account" />}
                    </Label>
                    <Select
                      components={{
                        Option: customOption,
                        SingleValue: customSingleValue,
                      }}
                      options={bankAccounts}
                      value={formData.selectedBankAccount}
                      onChange={(selected) =>
                        handleChange("selectedBankAccount", selected)
                      }
                      classNamePrefix="select"
                    />
                    {errors.selectedBankAccount && (
                      <h4 className="text-error no-drag">
                        {errors.selectedBankAccount}
                      </h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date).minDate}
                      maxDate={getMinMaxDates(formData.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                <Col md="6">
                  <CustomInput
                    autoFocus
                    name="amount"
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>
              </Row>

              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={loading || accountsLoading}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="funds_add_income" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(OtherIncomeHelpFromMuqamiTanzeem);
